'use client';

import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  ExtendedRefs,
  FloatingContext,
  OpenChangeReason,
  ReferenceType,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';

export type OpenChangeReasonEnriched = OpenChangeReason | 'button-cross-press';

export type UseModalOptions = {
  isOpen: boolean,
  onClose: () => void,
  onShow?: () => void,
  onOpenChange?: (open: boolean, event?: Event, reason?: OpenChangeReasonEnriched) => void,
  onOpenChangeComplete?: (open: boolean, event?: Event, reason?: OpenChangeReasonEnriched) => void,
};

export type UseModalReturn = {
  open: boolean,
  onOpenChange: (v: boolean, event?: Event, reason?: OpenChangeReasonEnriched) => void,
  getReferenceProps: (userProps?: React.HTMLProps<Element>) => Record<string, unknown>,
  getFloatingProps: (userProps?: React.HTMLProps<HTMLElement>) => Record<string, unknown>,
  getItemProps: (userProps?: React.HTMLProps<HTMLElement>) => Record<string, unknown>,
  context: FloatingContext<ReferenceType>,
  refs: ExtendedRefs<ReferenceType>,
  floatingStyles: React.CSSProperties,
  x: number,
  y: number,
  strategy: 'absolute' | 'fixed',
  placement: string,
  middlewareData: Record<string, unknown>,
  isPositioned: boolean,
  update: () => void,
  elements: {
    reference: ReferenceType | null,
    floating: HTMLElement | null,
    domReference: Element | null,
  },
  labelId: string | undefined,
  descriptionId: string | undefined,
  setLabelId: Dispatch<SetStateAction<string | undefined>>,
  setDescriptionId: Dispatch<SetStateAction<string | undefined>>,
};

export function useModal({
  isOpen: open,
  onClose,
  onShow,
  onOpenChange: onOpenChangeOuter,
  onOpenChangeComplete,
}: UseModalOptions): UseModalReturn {
  const [ labelId, setLabelId ] = useState<string | undefined>();
  const [ descriptionId, setDescriptionId ] = useState<string | undefined>();

  const isOpened = useRef(open);

  const setOpen = (v: boolean) => {
    if (!v) {
      isOpened.current = false;
      onClose?.();
    }
  };

  const onOpenChange = (
    v: boolean, event?: Event, reason?: OpenChangeReasonEnriched,
  ) => {
    if (event instanceof MouseEvent && event.button !== 0) {
      return;
    }
    setOpen(v);
    onOpenChangeOuter?.(
      v, event, reason,
    );
    onOpenChangeComplete?.(
      v, event, reason,
    );
  };

  const data = useFloating({
    open,
    onOpenChange,
  });

  useEffect(() => {
    if (!isOpened.current && open && onShow) {
      onShow();
      isOpened.current = true;
    } else if (isOpened.current && !open) {
      isOpened.current = false;
      onClose?.();
    }
  }, [
    open,
    onShow,
    onClose,
  ]);

  useEffect(() => {
    if (open && onShow) {
      onShow();
    }
  }, []);

  const context = data.context;

  const click = useClick(context, { enabled: open });
  const dismiss = useDismiss(context, { outsidePressEvent: 'mousedown', escapeKey: true });
  const role = useRole(context);

  const interactions = useInteractions([
    click,
    dismiss,
    role,
  ]);

  return useMemo(() => ({
    open,
    onOpenChange,
    ...interactions,
    ...data,
    labelId,
    descriptionId,
    setLabelId,
    setDescriptionId,
  }),
  [
    open,
    interactions,
    data,
    labelId,
    descriptionId,
  ]) as UseModalReturn;
}
